import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, MapPin } from 'lucide-react';

const CUSTOMER_NAMES = [
  'Anita', 'Priya', 'Sneha', 'Kavita', 'Neha', 'Pooja', 'Ritu', 'Meera',
  'Anjali', 'Divya', 'Radhika', 'Swati', 'Rekha', 'Sunita', 'Asha', 'Sapna'
];

const CITIES = [
  'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata',
  'Pune', 'Ahmedabad', 'Jaipur', 'Lucknow', 'Chandigarh', 'Indore'
];

export const SocialProofNotifications = () => {
  const [notification, setNotification] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [products, setProducts] = useState([]);
  const timeoutRef = useRef(null);
  const visibilityTimeoutRef = useRef(null);

  // Fetch real products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products?limit=30`);
        const data = await response.json();
        // Filter to only available products with valid data
        const availableProducts = (data.products || []).filter(p => 
          p.title && 
          p.image_urls && 
          p.image_urls.length > 0 && 
          p.price && 
          p.price > 0
        );
        setProducts(availableProducts);
      } catch (error) {
        console.error('Failed to fetch products for social proof:', error);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    if (products.length === 0) return;

    const showRandomNotification = () => {
      // Random timing between 8-20 seconds
      const delay = Math.random() * 12000 + 8000;

      timeoutRef.current = setTimeout(() => {
        const randomName = CUSTOMER_NAMES[Math.floor(Math.random() * CUSTOMER_NAMES.length)];
        const randomCity = CITIES[Math.floor(Math.random() * CITIES.length)];
        const randomProduct = products[Math.floor(Math.random() * products.length)];
        const randomTime = Math.floor(Math.random() * 30) + 5; // 5-35 minutes ago

        setNotification({
          name: randomName,
          city: randomCity,
          product: randomProduct.title,
          productImage: randomProduct.image_urls?.[0],
          timeAgo: `${randomTime} min ago`
        });

        setIsVisible(true);

        // Hide after 5 seconds
        visibilityTimeoutRef.current = setTimeout(() => {
          setIsVisible(false);
          // Schedule next notification
          showRandomNotification();
        }, 5000);
      }, delay);
    };

    // Start showing notifications after 3 seconds
    const initialTimer = setTimeout(showRandomNotification, 3000);

    return () => {
      clearTimeout(initialTimer);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (visibilityTimeoutRef.current) clearTimeout(visibilityTimeoutRef.current);
    };
  }, [products]);

  return (
    <AnimatePresence>
      {isVisible && notification && (
        <motion.div
          initial={{ x: -400, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -400, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 25 }}
          className="fixed bottom-20 sm:bottom-24 left-4 z-40 max-w-sm"
          data-testid="social-proof-notification"
        >
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-3 flex items-start gap-3">
            {notification.productImage ? (
              <img 
                src={notification.productImage} 
                alt="Product"
                className="w-12 h-12 object-cover rounded flex-shrink-0"
              />
            ) : (
              <div className="bg-green-100 rounded-full p-2 flex-shrink-0">
                <ShoppingBag className="w-5 h-5 text-green-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-gray-900">
                {notification.name} from {notification.city}
              </p>
              <p className="text-xs text-gray-600 mt-0.5 line-clamp-1">
                Purchased <span className="font-medium">{notification.product}</span>
              </p>
              <div className="flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3 text-gray-400" />
                <p className="text-[10px] text-gray-400">{notification.timeAgo}</p>
              </div>
            </div>
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0 mt-1"></div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};